import React, { useId, useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  Description as DescriptionIcon,
  MoreVert as MoreVertIcon,
  StopCircle as StopCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/userPermissions';
import StopPourDialog, { stopSuccessMessage } from '../pour/StopPourDialog';
import type { StopPourResult, StopPourTarget } from '../pour/StopPourDialog';
import ReportDialog from '../ReportDialog';
import { patchPour } from '../controlPanel/controlPanelSession';

export interface PourActionsMenuProps {
  pour: StopPourTarget;
  /** The pour is In Progress (scheduled pours included): Stop applies. */
  inProgress: boolean;
  /** The pour has readings to report on. */
  hasReadings: boolean;
  /** After a successful stop (the Control Panel snapshot is already
   *  patched). */
  onStopped?: (result: StopPourResult) => void;
  /** A message for the page's toast ("Pour stopped.", "Report generated"). */
  onMessage?: (text: string, severity: 'success' | 'error') => void;
  /** Extra styles for the button (for example color inherit in an AppBar). */
  sx?: SxProps<Theme>;
}

const ITEM_SX = { minHeight: 48 } as const;

/**
 * The phone layout's '...' menu for one pour, on Control Panel cards and the
 * dashboard header. Only Stop Pour and Generate report are offered on
 * phones, each only when the user may use it on this pour; with neither,
 * no button renders. It owns the Stop and Report dialogs.
 *
 * Render it outside any clickable area (a card's CardActionArea): React
 * events from the menu and dialogs bubble through their portals.
 */
const PourActionsMenu: React.FC<PourActionsMenuProps> = ({
  pour,
  inProgress,
  hasReadings,
  onStopped,
  onMessage,
  sx,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  // The dialogs fill the screen on phones; tablets get centred dialogs.
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [stopTarget, setStopTarget] = useState<StopPourTarget | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  const canStop = hasPermission(user, 'pours.stop') && inProgress;
  const canReport = hasPermission(user, 'reports.generate') && hasReadings;
  const menuOpen = anchorEl !== null;

  // An open dialog stays mounted even if the pour or the user's rights
  // change underneath it.
  if (!canStop && !canReport && !stopTarget && !reportOpen) return null;

  const openStop = () => {
    setAnchorEl(null);
    setStopTarget({ PourID: pour.PourID, PourCode: pour.PourCode, PourName: pour.PourName });
  };

  const openReport = () => {
    setAnchorEl(null);
    setReportOpen(true);
  };

  const handleStopped = (target: StopPourTarget, result: StopPourResult) => {
    setStopTarget(null);
    // The saved Control Panel list shows the pour stopped and reloads on
    // return. An already-stopped pour keeps its lock state (an admin may
    // have unlocked it).
    patchPour(
      target.PourID,
      result.already_stopped ? { PourStatus: 'Complete' } : { PourStatus: 'Complete', Locked: true },
    );
    onStopped?.(result);
    onMessage?.(stopSuccessMessage(result, t), 'success');
  };

  return (
    <>
      {(canStop || canReport) && (
        <IconButton
          aria-label={t('mobile.actions.menu', { defaultValue: 'Pour actions' })}
          aria-haspopup="true"
          aria-controls={menuOpen ? menuId : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
          sx={[{ width: 44, height: 44, flexShrink: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
          <MoreVertIcon />
        </IconButton>
      )}
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canStop && (
          <MenuItem onClick={openStop} sx={{ ...ITEM_SX, color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'error.main' }}>
              <StopCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('mobile.actions.stopPour', { defaultValue: 'Stop Pour' })}</ListItemText>
          </MenuItem>
        )}
        {canReport && (
          <MenuItem onClick={openReport} sx={ITEM_SX}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('mobile.actions.generateReport', { defaultValue: 'Generate report' })}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <StopPourDialog
        target={stopTarget}
        onClose={() => setStopTarget(null)}
        onStopped={handleStopped}
        fullScreen={fullScreen}
      />
      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        pourId={pour.PourID}
        pourCode={pour.PourCode || undefined}
        onSuccess={(message) => onMessage?.(message, 'success')}
        fullScreen={fullScreen}
        mobileLayout
      />
    </>
  );
};

export default PourActionsMenu;
